

import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { StoreView } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import {
  invalidateAdminSellers,
  invalidateAdminStores,
  invalidateAdminSubscriptions,
} from "@/lib/query/core/invalidation";
import { queryKeys } from "@/lib/query/core/query-keys";

type CreateSellerStoreInput = {
  sellerId: string;
  storeName: string;
  maxProductImageCount?: number;
  subscriptionPlanId: string;
  subscriptionReason: string;
  subscriptionStartDate?: string;
  subscriptionOverrideAmount?: number;
  subscriptionOverridePeriodDays?: number;
};

export function useCreateSellerStoreMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ sellerId, ...body }: CreateSellerStoreInput) =>
      apiJsonClient<StoreView>(endpoints.admin.sellerStores(sellerId), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: async (_data, variables) => {
      await Promise.all([
        invalidateAdminSellers(queryClient),
        invalidateAdminStores(queryClient),
        invalidateAdminSubscriptions(queryClient),
        queryClient.invalidateQueries({
          queryKey: queryKeys.admin.sellerById(variables.sellerId),
          exact: true,
        }),
      ]);
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}
